// Manager (ASM) — Retailer list, distributor-wise, ON-SCREEN VIEW.
// Was download-only (see git history) — the on-screen list was avoided
// because GET /retailers is zone-scoped, not scoped to the distributors
// actually mapped to this ASM, so "All distributors" showed every retailer
// in the zone (tens of thousands), not just this manager's own ~60-70
// mapped distributors. Fixed by always passing the mapped distributor_ids
// from the (already correctly-scoped) GET /distributors dropdown, never
// relying on the endpoint's zone-wide fallback.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Store, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import RetailerDetailsSheet from "@/components/RetailerDetailsSheet";

const PAGE_SIZE = 200; // backend's max page_size for /retailers

export default function RetailerListDownload() {
  const nav = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [beats, setBeats] = useState([]);
  const [beatId, setBeatId] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [detailsId, setDetailsId] = useState(null);

  useEffect(() => {
    api.get("/distributors", { params: { page_size: 200 } })
      .then(({ data }) => setDistributors(data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Beat options narrow to whichever distributor(s) are currently
  // selected — a distributor's own `beat_ids` array is the source (already
  // present on the /distributors response), no new endpoint needed.
  useEffect(() => {
    if (!distributors.length) { setBeats([]); return; }
    const pool = distributorId ? distributors.filter((d) => d.id === distributorId) : distributors;
    const beatIds = [...new Set(pool.flatMap((d) => d.beat_ids || []))];
    setBeatId("");
    if (!beatIds.length) { setBeats([]); return; }
    api.get("/beats", { params: { ids: beatIds.join(","), page_size: beatIds.length } })
      .then(({ data }) => setBeats(data.items || []))
      .catch(() => setBeats([]));
  }, [distributorId, distributors]);

  // Any filter change starts back at page 1 — a stale page number past the
  // end of a newly-narrowed result set would otherwise show an empty page.
  useEffect(() => { setPage(1); }, [distributorId, beatId]);

  useEffect(() => {
    if (!distributors.length && !distributorId) return;
    setLoading(true);
    const params = { page_size: PAGE_SIZE, page };
    if (distributorId) {
      params.distributor_id = distributorId;
    } else {
      // "All distributors" — restrict to this manager's own mapped set,
      // never the zone-wide fallback.
      params.distributor_ids = distributors.map((d) => d.id).join(",");
    }
    if (beatId) params.beat_id = beatId;
    api.get("/retailers", { params })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [distributorId, distributors, beatId, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="retailer-list-download-page">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500">← Back</button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Manager view</div>
        <h1 className="text-2xl font-extrabold text-neutral-900">Retailer list</h1>
        <p className="text-xs text-neutral-500 mt-0.5">View, by distributor or beat.</p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={distributorId || "__all__"}
                    onValueChange={(v) => setDistributorId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 max-w-[240px]" data-testid="retailer-list-distributor-select">
                <SelectValue placeholder="All distributors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All distributors (mapped)</SelectItem>
                {distributors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={beatId || "__all__"}
                    onValueChange={(v) => setBeatId(v === "__all__" ? "" : v)}
                    disabled={!beats.length}>
              <SelectTrigger className="h-9 max-w-[200px]" data-testid="retailer-list-beat-select">
                <SelectValue placeholder="All beats" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All beats</SelectItem>
                {beats.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!loading && (
            <div className="text-[11px] text-neutral-500" data-testid="retailer-list-total">
              {total} retailer{total === 1 ? "" : "s"}
            </div>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="py-10 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl" data-testid="retailer-list-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            No retailers found.
          </CardContent>
        </Card>
      )}

      {!loading && items.map((r) => (
        <button key={r.id} onClick={() => setDetailsId(r.id)}
                className="w-full text-left"
                data-testid={`retailer-list-row-${r.code}`}>
          <Card className="border-neutral-200 rounded-2xl hover:bg-neutral-50">
            <CardContent className="p-3 flex items-start gap-2">
              <Store className="w-4 h-4 mt-0.5 text-neutral-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-neutral-900 truncate">{r.name}</div>
                <div className="text-[11px] text-neutral-500">
                  {r.code}{r.city_name ? ` · ${r.city_name}` : ""}{r.distributor_name ? ` · ${r.distributor_name}` : ""}
                </div>
                {r.phone && <div className="text-[11px] text-neutral-500">{r.phone}</div>}
              </div>
            </CardContent>
          </Card>
        </button>
      ))}

      {!loading && total > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-1" data-testid="retailer-list-pagination">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="retailer-list-prev-page"
                  className="h-9 px-3 rounded-lg border border-neutral-200 text-sm text-neutral-700 disabled:opacity-40 flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <div className="text-xs text-neutral-500">Page {page} of {totalPages}</div>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  data-testid="retailer-list-next-page"
                  className="h-9 px-3 rounded-lg border border-neutral-200 text-sm text-neutral-700 disabled:opacity-40 flex items-center gap-1">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <RetailerDetailsSheet
        retailerId={detailsId}
        open={!!detailsId}
        onOpenChange={(v) => !v && setDetailsId(null)}
      />
    </div>
  );
}
